import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "../../../Components/StateProvider";
import React, { useEffect, useState, useMemo } from "react";
import { 
  FaEye, 
  FaEyeSlash, 
  FaFilter, 
  FaTimes, 
  FaFileExcel, 
  FaFilePdf, 
  FaPrint, 
  FaSearch, 
  FaChevronDown, 
  FaChevronUp, 
  FaSync, 
  FaInfoCircle,
  FaArrowUp,
  FaArrowDown
} from "react-icons/fa";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import {
  baseurl,
  ErrorAlert,
  GetDateTimeFormat,
  GetRecords,
  RupeeFormat,
  URLPRIFIX,
  WarningAlert,
} from "../../../functions/api";
import LoadingSpinner from "../../../Components/Elements/LoadingSpinner";

const AdvancedReconciliation = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const store = useStore();

  // Filter States
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [minAmount, setMinAmount] = useState("");
  const [maxAmount, setMaxAmount] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");

  // Functional States
  const [ledgerData, setLedgerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [expandedInvoices, setExpandedInvoices] = useState({});
  const [showFilters, setShowFilters] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Initialize
  useEffect(() => {
    store.setmodule("advancedreconciliation", { view: true });
    const dateTime = GetDateTimeFormat();
    const start = "2000-01-01";
    const end = dateTime.DBFormat.substring(0, 10);
    setFromDate(start);
    setToDate(end);
    fetchData(start, end, true);
  }, []);

  // Auto-refresh handler
  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        fetchData(fromDate, toDate);
      }, 30000); // refresh every 30 seconds
    }
    return () => clearInterval(interval);
  }, [autoRefresh, fromDate, toDate]);

  const fetchData = async (start, end, isInitialLoad = false) => {
    setLoading(true);
    try {
      // Fetch flat invoices with payments from the base reconciliation report API
      const response = await GetRecords(
        `/api/v2/accounting/reconciliation/${companyId}?startDate=${start}T00:00:00Z&endDate=${end}T23:59:59Z`
      );
      if (response.success) {
        // Group the flat invoices by customer on the client-side to preserve metrics structure
        const grouped = {};
        const list = response.record || [];
        
        let oldestDate = null;
        list.forEach(invoice => {
          if (invoice.invoiceDate) {
            const dStr = invoice.invoiceDate.split("T")[0];
            if (!oldestDate || dStr < oldestDate) {
              oldestDate = dStr;
            }
          }

          const custName = invoice.customerName || "Unknown Customer";
          const custId = invoice.customerId || 0;
          const ledgerName = invoice.ledgerName || "Accounts Receivable";
          
          if (!grouped[custName]) {
            grouped[custName] = {
              CustomerId: custId,
              CustomerName: custName,
              LedgerName: ledgerName,
              Invoices: []
            };
          }
          
          grouped[custName].Invoices.push({
            InvoiceId: invoice.invoiceId,
            InvoiceNo: invoice.invoiceNo,
            InvoiceDate: invoice.invoiceDate,
            InvoiceAmount: invoice.invoiceAmount,
            TotalPaid: invoice.totalPaid,
            Status: invoice.status,
            Payments: (invoice.payments || []).map(p => ({
              ReceiptId: p.receiptId,
              ReceiptNo: p.receiptNo,
              ReceiptDate: p.receiptDate,
              PaymentAmount: p.paymentAmount,
              PaymentType: p.paymentType,
              TransactionId: p.transaction_id,
              TransactionTypeId: p.transaction_type_id
            }))
          });
        });
        
        setLedgerData(Object.values(grouped));

        if (isInitialLoad && oldestDate) {
          setFromDate(oldestDate);
        }
      } else {
        setError(response.errorMessage || "Failed to load reconciliation records.");
      }
    } catch (err) {
      setError("Error fetching reconciliation data.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle helpers
  const toggleCustomer = (custId) => {
    setExpandedCustomers(prev => ({ ...prev, [custId]: !prev[custId] }));
  };

  const toggleInvoice = (invId) => {
    setExpandedInvoices(prev => ({ ...prev, [invId]: !prev[invId] }));
  };

  // Fetch unique customers list for dropdown filter
  const customerList = useMemo(() => {
    return [...new Set(ledgerData.map(item => item.CustomerName))].sort();
  }, [ledgerData]);

  // Client-side filtering & sorting
  const processedData = useMemo(() => {
    let filtered = ledgerData.map(customer => {
      // Filter invoices inside each customer
      const filteredInvoices = (customer.Invoices || []).filter(invoice => {
        // Status filter
        if (statusFilter !== "all" && invoice.Status !== statusFilter) return false;

        // Amount bounds filter
        const amt = invoice.InvoiceAmount || 0;
        if (minAmount && amt < Number(minAmount)) return false;
        if (maxAmount && amt > Number(maxAmount)) return false;

        return true;
      });

      // Calculate totals for customer based on filtered invoices
      const totalDebit = filteredInvoices.reduce((sum, inv) => sum + (inv.InvoiceAmount || 0), 0);
      const totalCredit = filteredInvoices.reduce((sum, inv) => sum + (inv.TotalPaid || 0), 0);
      const balance = totalDebit - totalCredit;

      return {
        ...customer,
        FilteredInvoices: filteredInvoices,
        TotalDebit: totalDebit,
        TotalCredit: totalCredit,
        Balance: balance
      };
    });

    // Root-level filters
    filtered = filtered.filter(customer => {
      // Customer filter
      if (customerFilter !== "all" && customer.CustomerName !== customerFilter) return false;

      // General search filter (Customer Name, Invoices info)
      if (search.trim()) {
        const q = search.toLowerCase();
        const nameMatch = customer.CustomerName?.toLowerCase().includes(q) || customer.LedgerName?.toLowerCase().includes(q);
        const invoiceMatch = customer.FilteredInvoices.some(inv => 
          inv.InvoiceNo?.toLowerCase().includes(q) || 
          inv.Payments?.some(p => p.ReceiptNo?.toLowerCase().includes(q))
        );
        if (!nameMatch && !invoiceMatch) return false;
      }

      // Hide customers with no matching invoices after filtering
      return customer.FilteredInvoices.length > 0;
    });

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.CustomerName.localeCompare(b.CustomerName);
        case "name_desc":
          return b.CustomerName.localeCompare(a.CustomerName);
        case "balance_desc":
          return b.Balance - a.Balance;
        case "balance_asc":
          return a.Balance - b.Balance;
        case "debit_desc":
          return b.TotalDebit - a.TotalDebit;
        case "credit_desc":
          return b.TotalCredit - a.TotalCredit;
        default:
          return 0;
      }
    });

    return filtered;
  }, [ledgerData, search, customerFilter, statusFilter, minAmount, maxAmount, sortBy]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [processedData, currentPage, pageSize]);

  const totalPages = Math.ceil(processedData.length / pageSize);

  // Dynamic KPI Card Calculations
  const kpis = useMemo(() => {
    let totalDebit = 0;
    let totalCredit = 0;
    let totalOutstanding = 0;
    let reconciledCount = 0;
    let partialCount = 0;
    let unpaidCount = 0;
    let transactionCount = 0;

    processedData.forEach(customer => {
      totalDebit += customer.TotalDebit;
      totalCredit += customer.TotalCredit;
      totalOutstanding += customer.Balance;

      customer.FilteredInvoices.forEach(inv => {
        transactionCount += 1;
        transactionCount += (inv.Payments || []).length;

        if (inv.Status === "Payment Completed") reconciledCount++;
        else if (inv.Status === "Partially Paid") partialCount++;
        else unpaidCount++;
      });
    });

    return {
      totalDebit,
      totalCredit,
      totalOutstanding,
      transactionCount,
      reconciledCount,
      partialCount,
      unpaidCount,
      customerCount: processedData.length
    };
  }, [processedData]);

  // Actions
  const handleApplyFilters = () => {
    setCurrentPage(1);
    fetchData(fromDate, toDate);
  };

  const handleClearFilters = () => {
    setSearch("");
    setCustomerFilter("all");
    setStatusFilter("all");
    setMinAmount("");
    setMaxAmount("");
    setSortBy("name_asc");
    const dateTime = GetDateTimeFormat();
    const start = "2000-01-01";
    const end = dateTime.DBFormat.substring(0, 10);
    setFromDate(start);
    setToDate(end);
    fetchData(start, end, true);
  };

  // Export to Excel
  const handleExportExcel = () => {
    const dataRows = [];

    // Header Metadata
    dataRows.push(["RECONCILIATION REPORT"]);
    dataRows.push(["Generated Date & Time:", new Date().toLocaleString()]);
    dataRows.push(["Selected Date Range:", `${fromDate} to ${toDate}`]);
    dataRows.push([]);
    dataRows.push(["S.No.", "Customer / Invoice No", "Document Date", "Type / Status", "Debit (Invoice Amount)", "Credit (Paid Amount)", "Outstanding Balance"]);

    let sNo = 1;
    processedData.forEach(customer => {
      dataRows.push([
        sNo++,
        customer.CustomerName,
        "-",
        `Ledger: ${customer.LedgerName}`,
        customer.TotalDebit,
        customer.TotalCredit,
        customer.Balance
      ]);

      customer.FilteredInvoices.forEach(inv => {
        dataRows.push([
          "",
          `  └─ ${inv.InvoiceNo}`,
          inv.InvoiceDate,
          `Invoice / ${inv.Status}`,
          inv.InvoiceAmount,
          inv.TotalPaid,
          inv.InvoiceAmount - inv.TotalPaid
        ]);

        if (inv.Payments && inv.Payments.length > 0) {
          inv.Payments.forEach(p => {
            dataRows.push([
              "",
              `      └─ ${p.ReceiptNo}`,
              p.ReceiptDate,
              `Payment / ${p.PaymentType}`,
              0,
              p.PaymentAmount,
              "-"
            ]);
          });
        }
      });
    });

    // Grand Totals Row
    dataRows.push([]);
    dataRows.push([
      "GRAND TOTAL",
      "",
      "",
      "",
      kpis.totalDebit,
      kpis.totalCredit,
      kpis.totalOutstanding
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reconciliation Report");
    XLSX.writeFile(workbook, `Reconciliation_Report_${fromDate}_to_${toDate}.xlsx`);
  };

  // Print Report
  const handlePrint = () => {
    window.print();
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF("p", "pt", "a4");
    const logo = "/Assets/images/Logo.png";

    // Header logo & branding
    try {
      doc.addImage(logo, "PNG", 40, 30, 45, 45);
    } catch (e) {
      // fallback if logo fails
    }

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text("ABSOLUTE LEGAL LAW FIRM", 100, 48);

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139); // slate-500
    doc.text("Premium Legal & Financial Reconciliation Dashboard", 100, 62);

    doc.setDrawColor(226, 232, 240); // slate-200
    doc.setLineWidth(1);
    doc.line(40, 90, 555, 90);

    // Document Meta
    doc.setFont("Helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59); // slate-800
    doc.text("Reconciliation Analysis Report", 40, 115);

    doc.setFont("Helvetica", "normal");
    doc.setFontSize(9);
    doc.text(`Selected Range: ${fromDate} to ${toDate}`, 40, 130);
    doc.text(`Generated On: ${new Date().toLocaleString()}`, 40, 142);

    // KPI Metrics Summary Box
    doc.setFillColor(248, 250, 252); // slate-50
    doc.roundedRect(40, 160, 515, 55, 6, 6, "F");

    doc.setFont("Helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text("TOTAL DEBIT (INVOICED)", 55, 178);
    doc.text("TOTAL CREDIT (RECONCILED)", 210, 178);
    doc.text("OUTSTANDING BALANCE", 380, 178);

    doc.setFontSize(12);
    doc.setTextColor(79, 70, 229); // Indigo
    doc.text(`Rs. ${RupeeFormat(kpis.totalDebit)}`, 55, 196);
    doc.setTextColor(16, 185, 129); // Emerald
    doc.text(`Rs. ${RupeeFormat(kpis.totalCredit)}`, 210, 196);
    doc.setTextColor(244, 63, 94); // Rose
    doc.text(`Rs. ${RupeeFormat(kpis.totalOutstanding)}`, 380, 196);

    // Content listings preview
    let yOffset = 250;
    doc.setFont("Helvetica", "bold");
    doc.text("Customer Summary", 40, yOffset);
    yOffset += 20;

    doc.setFont("Helvetica", "normal");
    processedData.slice(0, 15).forEach((cust) => {
      if (yOffset > 780) {
        doc.addPage();
        yOffset = 50;
      }
      doc.text(`${cust.CustomerName} | Ledger: ${cust.LedgerName}`, 40, yOffset);
      doc.text(`Debit: Rs. ${RupeeFormat(cust.TotalDebit)} | Credit: Rs. ${RupeeFormat(cust.TotalCredit)} | Bal: Rs. ${RupeeFormat(cust.Balance)}`, 40, yOffset + 15);
      yOffset += 35;
    });

    if (processedData.length > 15) {
      doc.text("... and more records. For full details, please print or export to Excel.", 40, yOffset);
    }

    doc.save(`Reconciliation_Report_${fromDate}_to_${toDate}.pdf`);
  };

  return (
    <div className="flex flex-col gap-6 p-6 bg-[#F8FAFC] min-h-screen text-slate-800">
      
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-5 rounded-2xl border border-slate-100 shadow-sm">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
            Advanced Reconciliation Analysis
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Reconcile ledger accounts against invoices, track credit payments, and calculate outstanding customer balances.
          </p>
        </div>

        <div className="flex flex-wrap gap-2.5 items-center">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-2 text-xs px-3.5 py-2 font-semibold rounded-xl border transition shadow-sm cursor-pointer ${
              autoRefresh 
                ? "bg-indigo-50 border-indigo-200 text-indigo-600 font-bold" 
                : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
            }`}
          >
            <FaSync className={`${autoRefresh ? "animate-spin" : ""}`} size={11} />
            {autoRefresh ? "Auto-Refresh On" : "Auto-Refresh"}
          </button>
          
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 text-xs px-3.5 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl hover:bg-slate-50 transition shadow-sm cursor-pointer"
          >
            <FaPrint size={11} /> Print
          </button>

          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 text-xs px-3.5 py-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl hover:bg-emerald-100 transition shadow-sm cursor-pointer font-semibold"
          >
            <FaFileExcel size={11} /> Excel Export
          </button>

          <button
            onClick={handleExportPDF}
            className="flex items-center gap-2 text-xs px-3.5 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl hover:bg-rose-100 transition shadow-sm cursor-pointer font-semibold"
          >
            <FaFilePdf size={11} /> PDF Export
          </button>
        </div>
      </div>

      {/* KPI Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Invoiced Debit Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Invoiced (Debit)</span>
            <span className="p-1 px-2 text-[10px] font-bold rounded-lg bg-indigo-50 text-indigo-600">Receivables</span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight">
              ₹ {RupeeFormat(kpis.totalDebit)}
            </h3>
            <p className="text-[10px] text-slate-500 mt-1 font-semibold">
              From {kpis.customerCount} Active Customers
            </p>
          </div>
        </div>

        {/* Reconciled Credit Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Reconciled Amount (Credit)</span>
            <span className="p-1 px-2 text-[10px] font-bold rounded-lg bg-emerald-50 text-emerald-600">Reconciled</span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl md:text-2xl font-black text-emerald-600 tracking-tight">
              ₹ {RupeeFormat(kpis.totalCredit)}
            </h3>
            <p className="text-[10px] text-slate-500 mt-1 font-semibold">
              {kpis.reconciledCount} Invoices Fully Settled
            </p>
          </div>
        </div>

        {/* Outstanding Balance Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Outstanding Balance</span>
            <span className="p-1 px-2 text-[10px] font-bold rounded-lg bg-rose-50 text-rose-600">Outstanding</span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl md:text-2xl font-black text-rose-600 tracking-tight">
              ₹ {RupeeFormat(kpis.totalOutstanding)}
            </h3>
            <p className="text-[10px] text-slate-500 mt-1 font-semibold">
              Unsettled Account Balances
            </p>
          </div>
        </div>

        {/* Transaction Summary Card */}
        <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Pending Reconciliation</span>
            <span className="p-1 px-2 text-[10px] font-bold rounded-lg bg-amber-50 text-amber-600">Status Count</span>
          </div>
          <div className="mt-4">
            <h3 className="text-xl md:text-2xl font-black text-amber-600 tracking-tight">
              {kpis.partialCount} Partial / {kpis.unpaidCount} Unpaid
            </h3>
            <p className="text-[10px] text-slate-500 mt-1 font-semibold">
              Out of {kpis.reconciledCount + kpis.partialCount + kpis.unpaidCount} Invoices Total
            </p>
          </div>
        </div>
      </div>

      {/* Advanced Filters Card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
        <div 
          onClick={() => setShowFilters(!showFilters)}
          className="p-4 border-b border-slate-100 flex justify-between items-center bg-[#FCFCFE] cursor-pointer hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <FaFilter className="text-indigo-600" size={12} />
            <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest">
              Advanced Reconciliation Filters
            </h2>
          </div>
          <button className="text-slate-400 hover:text-slate-600">
            {showFilters ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
          </button>
        </div>

        {showFilters && (
          <div className="p-5 flex flex-col gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              
              {/* Date Filters */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">From Date</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">To Date</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Customer Selector */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Customer Filter</label>
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                >
                  <option value="all">All Customers</option>
                  {customerList.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
              </div>

              {/* Reconciliation Status */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Reconciliation Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                >
                  <option value="all">All Statuses</option>
                  <option value="Payment Completed">Fully Reconciled</option>
                  <option value="Partially Paid">Partially Reconciled</option>
                  <option value="Unpaid">Unreconciled (Unpaid)</option>
                </select>
              </div>

              {/* Amount Range Filters */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Min Amount (₹)</label>
                <input
                  type="number"
                  placeholder="Min Rs."
                  value={minAmount}
                  onChange={(e) => setMinAmount(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Max Amount (₹)</label>
                <input
                  type="number"
                  placeholder="Max Rs."
                  value={maxAmount}
                  onChange={(e) => setMaxAmount(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Sort Options */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Sort Results By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                >
                  <option value="name_asc">Customer Name (A-Z)</option>
                  <option value="name_desc">Customer Name (Z-A)</option>
                  <option value="balance_desc">Highest Balance</option>
                  <option value="balance_asc">Lowest Balance</option>
                  <option value="debit_desc">Highest Debit</option>
                  <option value="credit_desc">Highest Credit</option>
                </select>
              </div>

              {/* Search String */}
              <div>
                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Text Search</label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-slate-400" size={11} />
                  </span>
                  <input
                    type="text"
                    placeholder="Search invoice, customer..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-8 p-2 border border-slate-200 rounded-xl text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

            </div>

            <div className="flex justify-end gap-2.5 mt-2 border-t border-slate-100 pt-4">
              <button
                onClick={handleClearFilters}
                className="text-xs px-5 py-2 border border-slate-200 text-slate-600 bg-white rounded-xl hover:bg-slate-50 transition cursor-pointer font-bold"
              >
                Clear Filters
              </button>
              <button
                onClick={handleApplyFilters}
                className="text-xs px-5 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition cursor-pointer font-bold"
              >
                Apply Range & Search
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Analysis Data Table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-[#FCFCFE]">
          <h2 className="text-xs font-black text-slate-700 uppercase tracking-widest">
            Reconciliation Analysis Records ({processedData.length})
          </h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 font-semibold">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
              className="text-xs border border-slate-200 rounded-lg px-2.5 py-1 focus:outline-none bg-white font-semibold"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse" style={{ minWidth: '1000px' }}>
            <thead>
              <tr className="bg-slate-50/70 border-b border-slate-200">
                <th className="w-12 text-center py-3.5 pl-4 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Expand
                </th>
                <th className="py-3.5 px-4 text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Customer / Ledger Name
                </th>
                <th className="py-3.5 px-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">
                  Total Debit (₹)
                </th>
                <th className="py-3.5 px-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">
                  Total Credit (₹)
                </th>
                <th className="py-3.5 px-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">
                  Outstanding Balance (₹)
                </th>
                <th className="py-3.5 px-4 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">
                  Reconciliation Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.map((customer) => {
                const custId = customer.CustomerId;
                const isCustExpanded = !!expandedCustomers[custId];
                const unreconciledBalance = customer.Balance;

                // Overall status logic for customer
                let overallStatus = "Reconciled";
                if (unreconciledBalance === customer.TotalDebit) {
                  overallStatus = "Unreconciled";
                } else if (unreconciledBalance > 0) {
                  overallStatus = "Partially Reconciled";
                }

                return (
                  <React.Fragment key={custId}>
                    {/* Customer group row */}
                    <tr 
                      onClick={() => toggleCustomer(custId)}
                      className="bg-white hover:bg-slate-50/50 cursor-pointer font-semibold transition duration-150"
                    >
                      <td className="py-3.5 text-center pl-4 text-slate-400">
                        {isCustExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="font-bold text-slate-800 text-xs">{customer.CustomerName}</div>
                        <div className="text-[10px] text-slate-400 font-semibold">Ledger: {customer.LedgerName}</div>
                      </td>
                      <td className="py-3.5 px-4 text-right text-xs font-bold text-slate-700">
                        {RupeeFormat(customer.TotalDebit)}
                      </td>
                      <td className="py-3.5 px-4 text-right text-xs font-bold text-emerald-600">
                        {RupeeFormat(customer.TotalCredit)}
                      </td>
                      <td className="py-3.5 px-4 text-right text-xs font-black text-rose-600">
                        {RupeeFormat(customer.Balance)}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-bold ${
                          overallStatus === "Reconciled" 
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200" 
                            : overallStatus === "Partially Reconciled"
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}>
                          {overallStatus}
                        </span>
                      </td>
                    </tr>

                    {/* Expandable transaction details area */}
                    {isCustExpanded && (
                      <tr>
                        <td colSpan={6} className="bg-slate-50/50 p-4 border-t border-b border-slate-100">
                          <div className="pl-6 border-l-2 border-indigo-400 flex flex-col gap-3">
                            <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400 flex items-center gap-1.5 mb-1">
                              <FaInfoCircle size={10} /> Invoices & Related Payment Transactions
                            </h4>

                            <table className="w-full text-left border border-slate-200/60 rounded-xl overflow-hidden bg-white shadow-sm">
                              <thead>
                                <tr className="bg-slate-50 text-[9px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-200/60">
                                  <th className="py-2.5 px-3">Date</th>
                                  <th className="py-2.5 px-3">Doc/Ref Number</th>
                                  <th className="py-2.5 px-3">Document Type</th>
                                  <th className="py-2.5 px-3 text-right">Debit (₹)</th>
                                  <th className="py-2.5 px-3 text-right">Credit (₹)</th>
                                  <th className="py-2.5 px-3 text-center">Status</th>
                                  <th className="py-2.5 px-3 text-center">Details</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100/60 text-xs">
                                {customer.FilteredInvoices.map((inv) => {
                                  const isInvExpanded = !!expandedInvoices[inv.InvoiceId];
                                  return (
                                    <React.Fragment key={inv.InvoiceId}>
                                      {/* Invoice row */}
                                      <tr className="hover:bg-slate-50/40">
                                        <td className="py-2.5 px-3 text-slate-500 font-semibold">
                                          {new Date(inv.InvoiceDate).toLocaleDateString("en-IN", {
                                            day: "numeric", month: "short", year: "numeric"
                                          })}
                                        </td>
                                        <td className="py-2.5 px-3 font-bold text-slate-700">
                                          {inv.InvoiceNo}
                                        </td>
                                        <td className="py-2.5 px-3 text-indigo-600 font-semibold">Invoice</td>
                                        <td className="py-2.5 px-3 text-right font-semibold text-slate-700">
                                          {RupeeFormat(inv.InvoiceAmount)}
                                        </td>
                                        <td className="py-2.5 px-3 text-right text-slate-400">-</td>
                                        <td className="py-2.5 px-3 text-center">
                                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${
                                            inv.Status === "Payment Completed" 
                                              ? "bg-emerald-50 text-emerald-700" 
                                              : inv.Status === "Partially Paid"
                                              ? "bg-amber-50 text-amber-700"
                                              : "bg-slate-100 text-slate-600"
                                          }`}>
                                            {inv.Status}
                                          </span>
                                        </td>
                                        <td className="py-2.5 px-3 text-center">
                                          {inv.Payments && inv.Payments.length > 0 && (
                                            <button 
                                              onClick={() => toggleInvoice(inv.InvoiceId)}
                                              className="text-[10px] font-bold text-indigo-600 hover:underline inline-flex items-center gap-1 cursor-pointer"
                                            >
                                              {isInvExpanded ? "Hide Payments" : `View Payments (${inv.Payments.length})`}
                                            </button>
                                          )}
                                        </td>
                                      </tr>

                                      {/* Payments details row */}
                                      {isInvExpanded && inv.Payments && inv.Payments.map((pay) => (
                                        <tr key={pay.ReceiptId} className="bg-slate-50/30 text-[11px]">
                                          <td className="py-2 px-3 text-slate-400 pl-8 font-semibold">
                                            {pay.ReceiptDate ? new Date(pay.ReceiptDate).toLocaleDateString("en-IN", {
                                              day: "numeric", month: "short", year: "numeric"
                                            }) : "--"}
                                          </td>
                                          <td className="py-2 px-3 font-semibold text-slate-500 pl-8">
                                            {pay.ReceiptNo || "--"}
                                          </td>
                                          <td className="py-2 px-3 text-slate-400">Payment receipt ({pay.PaymentType || "--"})</td>
                                          <td className="py-2 px-3 text-right text-slate-400">-</td>
                                          <td className="py-2 px-3 text-right text-emerald-600 font-bold">
                                            {RupeeFormat(pay.PaymentAmount)}
                                          </td>
                                          <td className="py-2 px-3 text-center text-slate-400">Reconciled</td>
                                          <td className="py-2 px-3"></td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

              {/* Totals row */}
              <tr className="bg-slate-50/60 font-black border-t-2 border-slate-200">
                <td className="py-4 text-center"></td>
                <td className="py-4 px-4 text-slate-800 font-black text-xs uppercase tracking-wider">
                  Grand Totals
                </td>
                <td className="py-4 px-4 text-right text-xs font-black text-slate-800">
                  ₹ {RupeeFormat(kpis.totalDebit)}
                </td>
                <td className="py-4 px-4 text-right text-xs font-black text-emerald-600">
                  ₹ {RupeeFormat(kpis.totalCredit)}
                </td>
                <td className="py-4 px-4 text-right text-xs font-black text-rose-600">
                  ₹ {RupeeFormat(kpis.totalOutstanding)}
                </td>
                <td className="py-4 px-4"></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pagination bar */}
        {processedData.length > 0 && (
          <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 bg-[#FCFCFE]">
            <span className="text-[11px] font-bold text-slate-500">
              Showing {Math.min((currentPage - 1) * pageSize + 1, processedData.length)}–{Math.min(currentPage * pageSize, processedData.length)} of {processedData.length} Customers
            </span>

            <div className="flex items-center gap-1">
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 cursor-pointer"
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              >
                First
              </button>
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 cursor-pointer"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, i, arr) => {
                  if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === "..." ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-xs text-gray-400">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-1.5 rounded-lg border text-xs font-bold transition cursor-pointer ${
                        currentPage === p
                           ? "text-white border-[#4F46E5]"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                      style={{ backgroundColor: currentPage === p ? '#4F46E5' : '#FFFFFF' }}
                    >
                      {p}
                    </button>
                  )
                )}

              <button
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 cursor-pointer"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button
                className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold hover:bg-slate-50 disabled:opacity-40 cursor-pointer"
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AdvancedReconciliation;
